import { RoleEnum } from 'src/app/core/models/permission-role.enum';
import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { HomeBox } from '../models/home-box.interface';
import { HomeBoxId } from '../models/home-box-id.enum';

// Content lives in the Squidex `home-v2026` schema.
const CONTENT = 'home-v2026';

// Leading '/' makes routerLink resolve from the app root (it is relative by default).
const ROOT = '/';

const INVENTORY_ROLES = [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];
const ADMIN_ROLES = [RoleEnum.Admin, RoleEnum.Agent];
const DSR_ROLES = [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.DsrAgent];
const DSR_BOX_ROLES = [...DSR_ROLES, RoleEnum.CmpAdmin];
const CONSENT_ROLES = [...ADMIN_ROLES, RoleEnum.CmpAdmin];
const WORKSPACE_ROLES = [RoleEnum.DsrManager, RoleEnum.DsrAgent, RoleEnum.Reviewer, RoleEnum.CmpAdmin];

/**
 * Static definition of the homepage dashboard boxes. Visibility is resolved at
 * runtime against the current user's role (see HomeDashboardComponent).
 */
export const HOME_BOXES: HomeBox[] = [
    {
        id: HomeBoxId.DataMapping,
        modifier: 'data-mapping',
        titleKey: `${CONTENT}.dataMappingTitle`,
        roles: INVENTORY_ROLES,
        items: [
            { icon: 'icon-cube', titleKey: `${CONTENT}.inventoryTitle`, subtitleKey: `${CONTENT}.inventorySubtitle`, route: [ROOT, RoutesManager.systems] },
            { icon: 'icon-discovery', titleKey: `${CONTENT}.radarTitle`, subtitleKey: `${CONTENT}.radarSubtitle`, route: [ROOT, RoutesManager.radar] },
            { icon: 'icon-type', titleKey: `${CONTENT}.dataClassificationTitle`, subtitleKey: `${CONTENT}.dataClassificationSubtitle`, route: [ROOT, RoutesManager.dataClassifier] },
        ],
        links: [
            { labelKey: `${CONTENT}.linkEmployeeAccess`, route: [ROOT, RoutesManager.employees] },
            // Custom fields -> company-settings/customization is admin-only.
            { labelKey: `${CONTENT}.linkCustomFields`, route: [ROOT, RoutesManager.companySettings, RoutesManager.customization], roles: ADMIN_ROLES },
        ],
    },
    {
        id: HomeBoxId.AiGovernance,
        modifier: 'ai',
        titleKey: `${CONTENT}.aiGovTitle`,
        roles: INVENTORY_ROLES,
        items: [
            { icon: 'icon-ai', titleKey: `${CONTENT}.aiAssessmentsTitle`, subtitleKey: `${CONTENT}.aiAssessmentsSubtitle`, route: [ROOT, RoutesManager.ai_assessments] },
            { icon: 'icon-ai', titleKey: `${CONTENT}.aiAssetsTitle`, subtitleKey: `${CONTENT}.aiAssetsSubtitle`, route: [ROOT, RoutesManager.ai_assets] },
        ],
        // "Skills" from the design is skipped — page not built yet.
    },
    {
        id: HomeBoxId.PrivacyRisk,
        modifier: 'gov',
        titleKey: `${CONTENT}.privacyRiskTitle`,
        roles: INVENTORY_ROLES,
        items: [
            { icon: 'icon-general', titleKey: `${CONTENT}.assessmentsTitle`, subtitleKey: `${CONTENT}.assessmentsSubtitle`, route: [ROOT, RoutesManager.compliance_assessments] },
            { icon: 'icon-pa', titleKey: `${CONTENT}.ropaTitle`, subtitleKey: `${CONTENT}.ropaSubtitle`, route: [ROOT, RoutesManager.ropa_assessments] },
            { icon: 'icon-cart', titleKey: `${CONTENT}.vendorAssessmentsTitle`, subtitleKey: `${CONTENT}.vendorAssessmentsSubtitle`, route: [ROOT, RoutesManager.vendor_assessments] },
        ],
        links: [
            { labelKey: `${CONTENT}.linkRiskRegistry`, route: [ROOT, RoutesManager.risk_registry] },
            { labelKey: `${CONTENT}.linkBusinessImpact`, route: [ROOT, RoutesManager.impactAssessment] },
            // Policies is admin-only.
            { labelKey: `${CONTENT}.linkPolicies`, route: [ROOT, RoutesManager.policies], roles: ADMIN_ROLES },
        ],
    },
    {
        id: HomeBoxId.Dsr,
        modifier: 'dsr',
        titleKey: `${CONTENT}.dsrTitle`,
        roles: DSR_BOX_ROLES,
        items: [
            // DSR agents/leads see only "Requests"; CMP admins only "Manage consent"; admins/owners the full box.
            { icon: 'icon-Opened-requests', titleKey: `${CONTENT}.requestsTitle`, subtitleKey: `${CONTENT}.requestsSubtitle`, route: [ROOT, RoutesManager.requests], roles: DSR_ROLES },
            { icon: 'icon-desk-bell', titleKey: `${CONTENT}.privacyCenterTitle`, subtitleKey: `${CONTENT}.privacyCenterSubtitle`, route: [ROOT, RoutesManager.privacyCenter], roles: ADMIN_ROLES },
            { icon: 'icon-Consent', titleKey: `${CONTENT}.manageConsentTitle`, subtitleKey: `${CONTENT}.manageConsentSubtitle`, route: [ROOT, RoutesManager.cmp, RoutesManager.cmpWebsites], roles: CONSENT_ROLES },
        ],
        links: [
            { labelKey: `${CONTENT}.linkPrivacyRights`, route: [ROOT, RoutesManager.dsr_setup, RoutesManager.privacy_rights], roles: ADMIN_ROLES },
            { labelKey: `${CONTENT}.linkBrandsTemplates`, route: [ROOT, RoutesManager.dsr_setup, RoutesManager.dsr_templates], roles: ADMIN_ROLES },
            { labelKey: `${CONTENT}.linkLanguages`, route: [ROOT, RoutesManager.dsr_setup, RoutesManager.dsr_localizations], roles: ADMIN_ROLES },
        ],
    },
    {
        id: HomeBoxId.Workspace,
        modifier: 'workspace',
        titleKey: `${CONTENT}.workspaceTitle`,
        roles: WORKSPACE_ROLES,
        items: [
            { icon: 'icon-tasks', titleKey: `${CONTENT}.myTasksTitle`, subtitleKey: `${CONTENT}.myTasksSubtitle`, route: [ROOT, RoutesManager.tasks] },
        ],
    },
];
